import { useMemo } from "react";
import { Copy, LoaderCircle, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Badge } from "~/atoms/badge";
import { Button } from "~/atoms/button";
import {
  Card,
  CardAction,
  CardFooter,
  CardHeader,
  CardTitle,
} from "~/atoms/card";
import {
  Item,
  ItemActions,
  ItemContent,
  ItemGroup,
  ItemTitle,
} from "~/atoms/item";

import { type WorkflowTemplateListItem } from "~/api/types";
import { cn } from "~/lib/utils";
import { Skeleton } from "~/atoms/skeleton";

export type SidebarTemplate = Pick<
  WorkflowTemplateListItem,
  "id" | "isDefault" | "name"
> & { isDraft?: boolean };

interface Props {
  templates: SidebarTemplate[];
  selectedTemplateId: number | null;
  duplicatingTemplateId: number | null;
  onSelect: (id: number) => void;
  onCreate: () => void;
  onDuplicate: (id: number) => void;
  onDelete: (id: number) => void;
}

export function WorkflowTemplateSidebar({
  templates,
  selectedTemplateId,
  duplicatingTemplateId,
  onSelect,
  onCreate,
  onDuplicate,
  onDelete,
}: Props) {
  const { t } = useTranslation();

  const sortedTemplates = useMemo(() => {
    const defaultTemplate = templates.find((t) => t.isDefault);
    const rest = templates.filter((t) => !t.isDefault);
    return defaultTemplate ? [defaultTemplate, ...rest] : rest;
  }, [templates]);

  return (
    <Card className="h-full min-h-0 gap-0 py-0">
      <div className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto">
        {sortedTemplates.length === 0 ? (
          <p className="text-muted-foreground px-4 py-6 text-center text-sm">
            {t(($) => $.workflowTemplates.noTemplates)}
          </p>
        ) : (
          <ItemGroup className="gap-0">
            {sortedTemplates.map(({ id, name, isDefault, isDraft }) => {
              const templateName =
                name ?? t(($) => $.workflowTemplates.defaultBadge);
              const isSelected = selectedTemplateId === id;

              return (
                <Item
                  key={id}
                  variant="outline"
                  className={cn(
                    "hover:bg-muted/50 h-11 cursor-pointer rounded-none border-x-0 border-t-0 py-0 last:border-b-0",
                    isSelected && "bg-muted",
                  )}
                  onClick={() => onSelect(id)}
                >
                  <ItemContent className="min-w-0">
                    <ItemTitle
                      className={cn("w-full min-w-0", {
                        italic: isDraft,
                      })}
                    >
                      <span className="min-w-0 truncate" title={templateName}>
                        {templateName}
                      </span>
                    </ItemTitle>
                  </ItemContent>

                  <ItemActions className="shrink-0">
                    {isDefault ? (
                      <Badge variant="secondary">
                        {t(($) => $.workflowTemplates.defaultBadge)}
                      </Badge>
                    ) : (
                      <Button
                        variant="ghost"
                        size="icon-xs"
                        title={t(($) => $.workflowTemplates.deleteTemplate)}
                        disabled={duplicatingTemplateId !== null}
                        onClick={(event) => {
                          event.stopPropagation();
                          onDelete(id);
                        }}
                      >
                        <Trash2 />
                      </Button>
                    )}
                    {!isDraft && (
                      <Button
                        variant="ghost"
                        size="icon-xs"
                        title={t(($) => $.workflowTemplates.duplicateTemplate)}
                        disabled={duplicatingTemplateId !== null}
                        onClick={(event) => {
                          event.stopPropagation();
                          onDuplicate(id);
                        }}
                      >
                        {duplicatingTemplateId === id ? (
                          <LoaderCircle className="animate-spin" />
                        ) : (
                          <Copy />
                        )}
                      </Button>
                    )}
                  </ItemActions>
                </Item>
              );
            })}
          </ItemGroup>
        )}
      </div>
      <CardFooter className="shrink-0 border-t p-3">
        <Button size="sm" className="w-full" onClick={onCreate}>
          {t(($) => $.commons.buttons.createWorkflowTemplate)}
        </Button>
      </CardFooter>
    </Card>
  );
}

export function WorkflowTemplateSidebarSkeleton() {
  return (
    <Card size="sm" className="h-full min-h-0 gap-0 py-0 shadow-sm">
      <CardHeader className="border-b py-3">
        <CardTitle>
          <Skeleton className="h-5 w-24" />
        </CardTitle>
        <CardAction>
          <Skeleton className="size-8 rounded-lg" />
        </CardAction>
      </CardHeader>

      <div className="min-h-0 flex-1">
        {Array.from({ length: 5 }).map((_, i) => (
          <div
            key={i}
            className="flex items-center border-b px-3 py-2.5 last:border-b-0"
          >
            <div className="min-w-0 flex-1">
              <Skeleton className="h-4 w-full" />
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}
